import { Option, Value } from 'baseui/select';
import Separator from 'components/common/separator';
import { useFormState } from 'components/elements/form';
import TableInput from 'components/elements/table/table-input';
import {
  ActionType,
  ColumnDataType,
  IColumn,
} from 'components/elements/table/type';
import Text from 'components/elements/text';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';

import { addNewAddOns } from '../../hooks/use-get-default-values';
import {
  ActivityPurchaseAddOnsItemFormType,
  ActivityPurchaseFormType,
} from '../form-type';

interface Props {
  parentIndex: number;
}

function useGetScheduleAddOnsTableList(parentIndex) {
  const { t } = useTranslation();
  const { editable } = useFormState();
  const { control } = useFormContext<ActivityPurchaseFormType>();
  const guestName =
    `activityPurchases.${parentIndex}.activityPurchaseGuests` as const;

  const guests = useWatch({
    control,
    name: guestName,
  });

  const columns: IColumn<ActivityPurchaseAddOnsItemFormType>[] =
    React.useMemo(() => {
      const guestList: Option[] = guests
        .filter((guest) => guest.guestId)
        .map((guest) => {
          return {
            id: guest.guestId,
            label: guest.guestName,
          };
        });

      return [
        {
          header: t('common:name'),
          accessorKey: 'guestId',
          minSize: 100,
          dataType: ColumnDataType.select,
          options: guestList,
          required: true,
        },
        {
          header: t('common:description'),
          accessorKey: 'description',
          minSize: 500,
          dataType: ColumnDataType.text,
        },
        {
          header: t('common:price'),
          accessorKey: 'price',
          minSize: 100,
          dataType: ColumnDataType.number,
          minNumber: 0,
          required: true,
        },
        ...(editable
          ? [
              {
                header: '',
                accessorKey: 'delete',
                minSize: 60,
                size: 60,
                maxSize: 60,
                dataType: ColumnDataType.delete,
              },
            ]
          : []),
      ];
    }, [editable, guests, t]);

  return columns;
}

export default function ActivityAddOnsTable(props: Props) {
  const { parentIndex } = props;
  const { t } = useTranslation();
  const { editable } = useFormState();

  const name =
    `activityPurchases.${parentIndex}.activityPurchaseAddOns` as const;

  const { getValues, trigger, control } = useFormContext();

  const { append, remove, update } = useFieldArray({
    control,
    name,
    keyName: '__customId',
  });

  const columns = useGetScheduleAddOnsTableList(parentIndex);

  const dispatch = React.useCallback(
    (action) => {
      const addons = getValues(name);
      const currentRowIndex = action.rowIndex as number;
      const addon = addons[currentRowIndex];
      const columnId =
        action.columnId as keyof ActivityPurchaseAddOnsItemFormType;
      const extra = action.extra as Value;
      const type = action.type as ActionType;
      const value = action.value;

      const otherVal: () => Partial<ActivityPurchaseAddOnsItemFormType> =
        () => {
          if (columnId === 'guestId') {
            return { guestName: extra?.[0]?.label as string };
          }
          return {};
        };
      if (type === 'addRow') {
        append(addNewAddOns());
        trigger(name);
      } else if (type === 'removeRow') {
        remove(currentRowIndex);
        trigger(name);
      } else if (type === 'update_cell') {
        update(currentRowIndex, {
          ...addon,
          [columnId]: value,
          ...otherVal(),
        });
        trigger(name);
      }
    },
    [append, getValues, name, remove, trigger, update],
  );

  return (
    <>
      <Text variant="ParagraphSmall">{t('ticketSales:add_ons')}</Text>
      <Separator gap={12} />
      <div style={{ width: '100%' }}>
        <TableInput
          columns={columns}
          data={getValues(name)}
          dispatch={dispatch}
          disabled={!editable}
          name={name}
          uniqueRowKey="customId"
        />
      </div>
    </>
  );
}
